import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, MessageCircle, Mail, CreditCard, FileSignature, Pencil, CheckCircle2, ExternalLink, Tablet, Link2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, errMsg } from "../lib/api";
import { Input } from "./ui/input";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { money, waLink } from "../lib/helpers";
import { StatusBadge } from "./Common";
import PaymentDialog from "./PaymentDialog";
import { SumUpDialog } from "./SumUpLinks";
import ConsentDialog from "./ConsentDialog";
import { ClinicalNotes } from "./ClinicalNotes";
import AppointmentForm from "./AppointmentForm";
import { ScreeningAlert } from "./HealthScreening";

const STATUSES = ["confirmed", "arrived", "completed", "no_show", "cancelled"];
const WA_TEXT = {
  en: (a, clinic) => `Hi ${a.client_name.split(" ")[0]}, this is ${clinic}. Reminder of your appointment for ${a.service_name} on ${a.date} at ${a.start_time} at ${a.location_name}. Reply to confirm. Thank you!`,
  it: (a, clinic) => `Ciao ${a.client_name.split(" ")[0]}, ti scriviamo da ${clinic}. Ti ricordiamo l'appuntamento per ${a.service_name} il ${a.date} alle ${a.start_time} presso ${a.location_name}. Rispondi per confermare. Grazie!`,
};

export function NeedsToKnow({ client }) {
  const { t } = useT();
  const items = [["allergies", client.allergies], ["medications", client.medications], ["conditions", client.conditions], ["attention", client.attention_notes]].filter(([, v]) => v);
  if (!items.length) return null;
  return (
    <div data-testid="client-needs-to-know-box" className="rounded-[var(--radius)] border border-amber-500/40 bg-amber-500/10 p-4 text-amber-900">
      <div className="flex items-center gap-2 font-semibold text-sm mb-2"><AlertTriangle className="h-4 w-4" />{t("needs_to_know")}</div>
      <dl className="grid gap-1 text-sm">{items.map(([k, v]) => <div key={k}><dt className="inline font-medium">{t(k)}: </dt><dd className="inline">{v}</dd></div>)}</dl>
    </div>
  );
}

export default function AppointmentDrawer({ id, onClose, onChanged }) {
  const { can, settings } = useApp();
  const { t, lang } = useT();
  const [a, setA] = useState(null);
  const [notes, setNotes] = useState("");
  const [share, setShare] = useState(false);
  const [pay, setPay] = useState(false);
  const [sumup, setSumup] = useState(false);
  const [consent, setConsent] = useState(false);
  const [edit, setEdit] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    api.get(`/appointments/${id}`).then((r) => { setA(r.data); setNotes(r.data.clinical_notes || ""); setShare(!!r.data.share_notes); });
  }, [id]);
  useEffect(() => { setA(null); load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };
  const setStatus = async (status) => {
    try { await api.patch(`/appointments/${id}/status`, { status }); toast.success(t(`status_${status}`)); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveNotes = async () => {
    try { await api.patch(`/appointments/${id}/clinical`, { clinical_notes: notes, share_with_patient: share }); toast.success("Notes saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sendEmail = async () => {
    try { await api.post(`/appointments/${id}/send-confirmation`); toast.success("Confirmation email sent"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const whatsapp = async () => {
    if (settings.whatsapp_connected && a.client?.whatsapp_opt_in) {
      try { await api.post(`/appointments/${id}/whatsapp`, { kind: "reminder" }); toast.success("WhatsApp reminder sent"); }
      catch (e) { toast.error(errMsg(e)); }
      return;
    }
    const l = a.client?.preferred_language === "it" ? "it" : "en";
    window.open(waLink(a.client_phone, WA_TEXT[l](a, settings.branding?.clinic_name || "our clinic")), "_blank");
    api.post(`/appointments/${id}/log-message`, { channel: "whatsapp", kind: "reminder" });
  };

  const balance = a ? Math.max(a.price - (a.paid_total || 0), 0) : 0;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="appointment-drawer">
        {!a ? <p className="text-sm text-muted-foreground p-4">Loading…</p> : (
          <div className="space-y-6">
            <SheetHeader>
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{a.date} · {a.start_time}–{a.end_time}</p>
              <SheetTitle className="text-2xl font-display" data-testid="drawer-client-name">{a.client_name}</SheetTitle>
              {can("clients.write") && <a href={`/checkin/${a.client_id}?appt=${a.id}`} data-testid="drawer-checkin-btn" className="text-sm text-primary flex items-center gap-1"><Tablet className="h-4 w-4" />Check-in on this device</a>}
              <p className="text-sm text-muted-foreground">{a.service_name} · {a.professional_name} · {a.location_name}</p>
              <div className="pt-1"><StatusBadge status={a.status} testId="drawer-status-badge" /></div>
            </SheetHeader>

            {a.client && <NeedsToKnow client={a.client} />}
            {a.client && <ScreeningAlert client={a.client} />}

            {a.consent_status !== "not_required" && (
              <div data-testid="drawer-consent-status" className={`flex items-center justify-between rounded-[var(--radius)] border p-3 text-sm ${a.consent_status === "ok" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-800" : "border-amber-500/40 bg-amber-500/10 text-amber-900"}`}>
                <span className="flex items-center gap-2">{a.consent_status === "ok" ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                  {a.consent_status === "ok" ? t("signed") : t("consent_missing")}</span>
                {a.consent_status === "missing" && can("consents.sign") && (
                  <Button size="sm" variant="outline" data-testid="drawer-request-consent-btn" onClick={() => setConsent(true)}><FileSignature className="h-3.5 w-3.5 mr-1" />{t("request_consent")}</Button>
                )}
              </div>
            )}

            {can("agenda.write") && (
              <div className="flex flex-wrap gap-2" data-testid="drawer-status-pills">
                {STATUSES.map((s) => (
                  <button key={s} data-testid={`status-pill-${s}`} onClick={() => setStatus(s)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${a.status === s ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>
                    {t(`status_${s}`)}
                  </button>
                ))}
              </div>
            )}

            {a.visit_type === "home" && (
              <div className="rounded-[var(--radius)] border bg-primary/5 p-3 text-sm space-y-2" data-testid="drawer-home-visit">
                <div className="font-semibold">Home visit</div>
                <a className="text-primary underline" target="_blank" rel="noreferrer" data-testid="drawer-map-link" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${a.address} ${a.postcode}`)}`}>{a.address}, {a.postcode}</a>
                {can("agenda.write") && <div className="flex items-center gap-2">Travel fee
                  <Input type="number" min={0} className="w-24 h-8" data-testid="drawer-travel-fee" defaultValue={a.travel_fee} key={a.travel_fee}
                    onBlur={(e) => Number(e.target.value) !== a.travel_fee && api.patch(`/home/appointments/${a.id}/travel-fee`, { travel_fee: Number(e.target.value) || 0 }).then(() => { toast.success("Travel fee updated"); refresh(); })} />
                  {a.travel_fee_manual && <span className="text-xs text-muted-foreground">changed manually</span>}</div>}
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 rounded-[var(--radius)] border bg-muted/30 p-4 text-sm" data-testid="drawer-payment-summary">
              <div><div className="text-xs text-muted-foreground">{t("price")}</div><div className="font-semibold">{money(a.price, a.currency, lang)}</div></div>
              <div><div className="text-xs text-muted-foreground">{t("paid")}</div><div className="font-semibold">{money(a.paid_total, a.currency, lang)}</div></div>
              <div><div className="text-xs text-muted-foreground">{t("balance")}</div><div className="font-semibold">{money(balance, a.currency, lang)}</div></div>
              {a.deposit_amount > 0 && <div className="col-span-3 text-xs text-muted-foreground">{t("deposit")}: {money(a.deposit_amount, a.currency, lang)} {a.deposit_paid ? "✓" : ""}</div>}
            </div>

            <div className="flex flex-wrap gap-2">
              {can("payments.write") && <Button size="sm" className="rounded-full" data-testid="drawer-take-payment-btn" onClick={() => setPay(true)}><CreditCard className="h-4 w-4 mr-1.5" />{t("take_payment")}</Button>}
              {can("payments.write") && balance > 0 && <Button size="sm" variant="outline" className="rounded-full" data-testid="drawer-sumup-link-btn" onClick={() => setSumup(true)}><Link2 className="h-4 w-4 mr-1.5" />SumUp</Button>}
              {can("messages.send") && a.client_phone && <Button size="sm" variant="outline" className="rounded-full" data-testid="drawer-whatsapp-btn" onClick={whatsapp}><MessageCircle className="h-4 w-4 mr-1.5" />{t("whatsapp")}</Button>}
              {can("messages.send") && a.client_email && <Button size="sm" variant="outline" className="rounded-full" data-testid="drawer-email-btn" onClick={sendEmail}><Mail className="h-4 w-4 mr-1.5" />{t("email_btn")}</Button>}
              {can("consents.sign") && a.client && <Button size="sm" variant="outline" className="rounded-full" data-testid="drawer-consent-btn" onClick={() => setConsent(true)}><FileSignature className="h-4 w-4 mr-1.5" />Consent form</Button>}
              {can("agenda.write") && <Button size="sm" variant="ghost" className="rounded-full" data-testid="drawer-edit-btn" onClick={() => setEdit(true)}><Pencil className="h-4 w-4 mr-1.5" />{t("edit")}</Button>}
              {can("clients.read") && <Link to={`/app/clients/${a.client_id}`} data-testid="drawer-client-link" className="inline-flex items-center text-sm text-primary px-2"><ExternalLink className="h-4 w-4 mr-1" />{t("client")}</Link>}
            </div>

            {a.notes && <div className="text-sm"><div className="text-xs text-muted-foreground mb-1">{t("notes")}</div>{a.notes}</div>}

            {a.client && can("clinical.write") && (
              <div className="space-y-2 border-t pt-4" data-testid="drawer-clinical-section">
                <div className="text-xs font-semibold text-muted-foreground">{t("clinical_notes")}</div>
                <ClinicalNotes client={a.client} appointmentId={a.id} />
              </div>
            )}

            {"clinical_notes" in a && (a.clinical_notes || !can("clinical.write")) && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">Quick notes</div>
                <Textarea data-testid="drawer-clinical-notes" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="drawer-share-notes-checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                  Share these notes with the patient (visible in their patient app)
                </label>
                <Button size="sm" variant="outline" data-testid="drawer-save-notes-btn" onClick={saveNotes}>{t("save")}</Button>
              </div>
            )}
          </div>
        )}
        {a && (
          <>
            <PaymentDialog open={pay} onOpenChange={setPay} clientId={a.client_id} locationId={a.location_id} appointment={a} defaultAmount={balance} currency={a.currency} onSaved={refresh} />
            <SumUpDialog open={sumup} onOpenChange={setSumup} appointment={a} balance={balance} onPaid={refresh} />
            {a.client && <ConsentDialog open={consent} onOpenChange={setConsent} client={a.client} appointmentId={a.id} onDone={load} />}
            <AppointmentForm open={edit} onOpenChange={setEdit} appointment={a} onSaved={refresh} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
